import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ApiError, get, post, qs } from '../lib/api';
import { navigate, storageGet, storageSet, useDebounced, useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { CARRIERS, dayKey, formatDateTime, formatTime, isValidImei } from '../../shared/format';
import { formatCents } from '../../shared/money';
import {
  SICKW_GROUP_LABELS, cleanDeviceId, deviceIdProblem, parseUnlockSites, sickwPriceLabel,
  type SickwGroup, type SickwService, type UnlockSite,
} from '../../shared/phone-check';
import { phoneFromCheck, type CheckedPhone, type ModelList } from '../../shared/sickw-phone';
import { UNLOCK_STATUS, unlockPriceLabel, type UnlockStatus } from '../../shared/unlock';
import { Badge, Button, Field, Sheet, Spinner, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { usePrint } from '../ui/print';
import { StickerEditor, batteryProblem, lastPriceLike, printPriceStickers, stickerFromDraft, stickerName, type PricedLike, type StickerDraft } from './PriceSticker';
import { AddDeviceSheet, type DevicePrefill } from './Inventory';
import { usePlan } from '../lib/plan';

/**
 * Phone check: SICKW checks (iCloud, carrier, blacklist, Samsung, Pixel...) without leaving
 * STORE OS. SICKW blocks its site from showing inside other apps, so the checks run through
 * its API on the server and the results show here. Unlock sites (GSM Unlockers, AT&T...)
 * open in their own window beside STORE OS, with the IMEI copied, ready to paste. On the sites
 * connected in Settings (GSM Unlockers, HangUnlock), unlocks are ordered right here and the
 * code shows here when it's ready.
 */

interface ServiceOption extends SickwService {
  last_price: string | null;
}
interface Setup {
  ready: boolean;
  can_set_up: boolean;
  buy_service: string | null;
  services: ServiceOption[];
}
interface CheckLine {
  label: string;
  value: string;
  tone: 'good' | 'bad' | 'warn' | null;
}
interface PhoneCheck {
  id: string;
  imei: string;
  service: { id: string; name: string };
  status: 'ok' | 'error';
  verdict: string;
  lines?: CheckLine[];
  text: string;
  price: string | null;
  balance: string | null;
  created_at: number;
  employee_name: string | null;
  location_name: string | null;
  reused?: boolean;
}

/** One phone in a "Check many at once" run. */
interface BulkRow {
  imei: string;
  state: 'waiting' | 'working' | 'done' | 'failed';
  check: PhoneCheck | null;
  error: string | null;
}

const VERDICT: Record<string, { label: string; tone: 'green' | 'red' | 'amber' }> = {
  clean: { label: 'Clean', tone: 'green' },
  blocked: { label: 'Blacklisted, lost or stolen', tone: 'red' },
  fmi_on: { label: 'Find My / iCloud is ON', tone: 'red' },
  error: { label: 'Didn’t work', tone: 'amber' },
};

const LAST_SERVICE = 'sos_phone_check_service';
const GROUP_ORDER: SickwGroup[] = ['apple', 'carrier', 'android', 'other'];

const priceOf = (s: ServiceOption) => sickwPriceLabel(s.last_price ?? s.price);
const when = (ms: number) => (dayKey(ms) === dayKey(Date.now()) ? `today at ${formatTime(ms)}` : formatDateTime(ms));

// ---------------------------------------------------------------- opening it from anywhere

interface PhoneCheckCtx {
  /** Opens Phone check, with this IMEI or serial typed in. */
  open: (imei?: string) => void;
  /** False for roles that can't check in repairs or buy phones. */
  available: boolean;
}

const Ctx = createContext<PhoneCheckCtx>({ open: () => undefined, available: false });

export function PhoneCheckProvider({ children }: { children: ReactNode }) {
  const { can } = useSession();
  // A phone shop's tool (IMEI checks on SICKW, the unlock sites): not at a computer, auto or vacuum shop.
  const plan = usePlan();
  const available = (can('repair.create') || can('buyback.create')) && plan.industry === 'cellphone';
  const [request, setRequest] = useState<{ imei: string; n: number } | null>(null);
  const open = useCallback((imei?: string) => setRequest((r) => ({ imei: imei ?? '', n: (r?.n ?? 0) + 1 })), []);
  const value = useMemo(() => ({ open, available }), [open, available]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {request && available ? <PhoneCheckSheet key={request.n} initialImei={request.imei} onClose={() => setRequest(null)} /> : null}
    </Ctx.Provider>
  );
}

export function usePhoneCheck() {
  return useContext(Ctx);
}

/** Phone with a magnifier, drawn like the other STORE OS icons. */
export function PhoneCheckIcon() {
  return (
    <svg className="svg-ic" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="3" width="10" height="18" rx="2" />
      <path d="M7.5 17.5h2" />
      <circle cx="16.5" cy="10.5" r="3.2" />
      <path d="M18.9 12.9 21.5 15.5" />
    </svg>
  );
}

/** The Check button beside an IMEI box: opens Phone check with what's typed. */
export function PhoneCheckButton({ code, label = 'Check', size }: { code: string; label?: string; size?: 'sm' | 'md' }) {
  const pc = usePhoneCheck();
  if (!pc.available) return null;
  return (
    <Button size={size} icon="search" onClick={() => pc.open(code)} title="Check this phone on SICKW: iCloud, carrier, blacklist and more">
      {label}
    </Button>
  );
}

/** "Phone check" in the side menu (tablets and computers). */
export function PhoneCheckNavItem() {
  const pc = usePhoneCheck();
  if (!pc.available) return null;
  return (
    <button type="button" className="nav-item" onClick={() => pc.open()} title="Check a phone on SICKW and open the unlock sites">
      <PhoneCheckIcon />
      <span>Phone check</span>
    </button>
  );
}

/** "Phone check" on the More screen (phones). */
export function PhoneCheckListRow() {
  const pc = usePhoneCheck();
  if (!pc.available) return null;
  return (
    <button type="button" className="list-row" onClick={() => pc.open()}>
      <PhoneCheckIcon />
      <span className="main-col t">Phone check (SICKW) & unlock sites</span>
      <Icon.chevronRight />
    </button>
  );
}

// ---------------------------------------------------------------- unlock sites

/** Copies text in the same tap (before another window takes the focus). */
function copyText(text: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.top = '0';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  ta.remove();
  return ok;
}

// One window per unlock site, kept outside React so it survives closing Phone check.
const siteWindows = new Map<string, Window>();

/** Opens a site in its own window on the right side of the screen, or brings it back to the front. */
function openBeside(url: string, name: string): boolean {
  const key = name.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  const open = siteWindows.get(key);
  if (open && !open.closed) {
    open.focus();
    return true;
  }
  const scr = window.screen as Screen & { availLeft?: number; availTop?: number };
  const sw = scr.availWidth || window.outerWidth;
  const sh = scr.availHeight || window.outerHeight;
  const width = Math.min(Math.max(Math.round(sw * 0.55), 900), 1200, Math.max(sw - 380, 600));
  const left = (scr.availLeft ?? 0) + Math.max(0, sw - width);
  const top = scr.availTop ?? 0;
  const win = window.open(url, `storeos_unlock_${key}`, `popup=yes,width=${width},height=${sh},left=${left},top=${top}`);
  if (!win) return false;
  try {
    win.opener = null; // the site gets no handle back into STORE OS
  } catch {
    // The window was already showing the site; nothing to cut.
  }
  siteWindows.set(key, win);
  return true;
}

/** The unlock sites from Settings, each a button. The IMEI (when there is one) is copied as it opens. */
export function UnlockSites({ imei, onLeave }: { imei?: string; onLeave?: () => void }) {
  const { me, can } = useSession();
  const toast = useToast();
  const sites = useMemo(() => parseUnlockSites(me?.settings?.unlock_links), [me?.settings?.unlock_links]);
  const id = cleanDeviceId(imei);
  const usable = !!id && !deviceIdProblem(id);
  const owner = can('settings.manage');
  if (!sites.length) {
    return owner ? <p className="small muted">No unlock sites yet. Add them in <a href="#/settings/business" onClick={onLeave}>Settings → Business &amp; receipts</a>.</p> : null;
  }
  const go = (e: MouseEvent<HTMLAnchorElement>, site: UnlockSite) => {
    if (!site.url) return;
    const copied = usable ? copyText(id) : false;
    // In its own window beside STORE OS; if pop-ups are blocked, the link opens a new tab instead.
    if (openBeside(site.url, site.name)) e.preventDefault();
    if (copied) toast(`IMEI copied. Paste it on ${site.name}.`, 'good');
  };
  return (
    <div className="unlock-sites">
      {sites.map((site) =>
        site.url ? (
          <a key={site.name} className="btn btn-secondary unlock-site" href={site.url} target="_blank" rel="noreferrer" onClick={(e) => go(e, site)}>
            <Icon.unlock />
            <span className="grow">{site.name}</span>
            <Icon.arrowRight />
          </a>
        ) : (
          <span key={site.name} className="unlock-site unlock-site-off">
            <Icon.unlock />
            <span className="grow">{site.name}</span>
            {owner ? <a href="#/settings/business" onClick={onLeave}>Add its link</a> : <span className="small">No link yet</span>}
          </span>
        ),
      )}
    </div>
  );
}

/** On "Unlock, reset or software": the unlock sites, right under the IMEI box. */
export function UnlockTools({ code }: { code: string }) {
  const pc = usePhoneCheck();
  const setup = useLoad<UnlockSetupInfo>(() => get('/api/unlock/setup'), []);
  const id = cleanDeviceId(code);
  const usable = !!id && !deviceIdProblem(id);
  const ordering = (setup.data?.sites ?? []).filter((s) => s.connected);
  return (
    <div className="unlock-tools">
      <div className="small strong">Unlock sites</div>
      <p className="small muted">
        {usable ? 'Each opens beside STORE OS with the IMEI copied, ready to paste.' : 'Each opens beside STORE OS. Type or scan the IMEI first and it’s copied for you.'}
        {' '}Check the phone first with <span className="strong">Check</span> next to the IMEI.
      </p>
      <UnlockSites imei={code} />
      {ordering.length && pc.available ? (
        <div className="row-wrap">
          <Button variant="primary" icon="unlock" onClick={() => pc.open(code)}>Order the unlock in STORE OS</Button>
          <span className="small muted">On {ordering.map((s) => s.name).join(' or ')}, paid from your credit there. The code shows in Phone check.</span>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- ordering unlocks (GSM Unlockers, HangUnlock)

interface UnlockSiteInfo {
  slot: string;
  name: string;
  url: string | null;
  connected: boolean;
}
interface UnlockSetupInfo {
  sites: UnlockSiteInfo[];
  can_set_up: boolean;
}
interface UnlockFieldInfo {
  name: string;
  label: string;
  kind: 'text' | 'select';
  options: string[];
  required: boolean;
}
interface UnlockServiceInfo {
  id: string;
  name: string;
  group: string;
  price: string | null;
  time: string;
  info: string;
  fields: UnlockFieldInfo[];
  site_only: string | null;
}
interface UnlockServiceList {
  services: UnlockServiceInfo[];
  currency: string | null;
  fetched_at: number;
  stale: boolean;
  error: string | null;
}
interface UnlockBalanceInfo {
  balance: string | null;
  currency: string | null;
  error: string | null;
}
interface UnlockOrderInfo {
  id: string;
  site: string;
  site_name: string;
  imei: string;
  service: { id: string; name: string };
  price: string | null;
  currency: string | null;
  reference: string | null;
  status: UnlockStatus;
  code: string | null;
  error: string | null;
  note: string | null;
  created_at: number;
  checked_at: number | null;
  done_at: number | null;
  employee_name: string | null;
}

const LAST_UNLOCK_SITE = 'sos_unlock_site';
const LAST_UNLOCK_SERVICE = 'sos_unlock_service';

/** One per tap of "Yes, order it": the server never orders the same tap twice. */
function newRequestId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  }
}

/** Order an unlock on GSM Unlockers or HangUnlock without leaving STORE OS; the code shows here when it's ready. */
function UnlockOrders({ imei, onLeave }: { imei: string; onLeave: () => void }) {
  const setup = useLoad<UnlockSetupInfo>(() => get('/api/unlock/setup'), []);
  const [slot, setSlot] = useState(() => storageGet<string>(LAST_UNLOCK_SITE, ''));
  const orders = useLoad<UnlockOrderInfo[]>(() => get(`/api/unlock-orders${qs({ imei: imei || undefined, limit: 10 })}`), [imei]);
  const reloadOrders = orders.reload;
  const open = (orders.data ?? []).some((o) => o.status === 'sending' || o.status === 'waiting' || o.status === 'working');
  // While an order is open, look again every minute (STORE OS asks the site every few minutes).
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(reloadOrders, 60_000);
    return () => clearInterval(t);
  }, [open, reloadOrders]);

  if (!setup.data) return null;
  const sites = setup.data.sites.filter((s) => s.connected);
  const site = sites.find((s) => s.slot === slot) ?? sites[0] ?? null;
  const pickSite = (next: string) => {
    setSlot(next);
    storageSet(LAST_UNLOCK_SITE, next);
  };
  return (
    <div className="stack-sm">
      <span className="small strong">Order an unlock</span>
      {!site ? (
        setup.data.can_set_up ? (
          <p className="small muted">
            Order unlocks right here on your {setup.data.sites.map((s) => s.name).join(' and ')} accounts: add each site’s username and API key in{' '}
            <a href="#/settings/buy" onClick={onLeave}>Settings → Buy &amp; trade-in</a>.
          </p>
        ) : (
          <p className="small muted">Ordering unlocks in STORE OS isn’t set up yet. Use the unlock sites above.</p>
        )
      ) : (
        <>
          {sites.length > 1 ? (
            <div className="chips" role="group" aria-label="Unlock site">
              {sites.map((s) => (
                <button key={s.slot} type="button" className="chip" aria-pressed={site.slot === s.slot} onClick={() => pickSite(s.slot)}>{s.name}</button>
              ))}
            </div>
          ) : null}
          <UnlockOrderForm key={site.slot} site={site} imei={imei} onOrdered={reloadOrders} />
        </>
      )}
      {orders.data?.length ? (
        <>
          <span className="small strong">{imei ? 'Unlock orders for this phone' : 'Latest unlock orders'}</span>
          <div className="stack-sm">
            {orders.data.map((o) => <UnlockOrderCard key={o.id} order={o} showImei={!imei} onChanged={reloadOrders} />)}
          </div>
        </>
      ) : null}
    </div>
  );
}

function UnlockOrderForm({ site, imei, onOrdered }: { site: UnlockSiteInfo; imei: string; onOrdered: () => void }) {
  const toast = useToast();
  const [fresh, setFresh] = useState(0);
  const list = useLoad<UnlockServiceList>(() => get(`/api/unlock/services${qs({ site: site.slot, fresh: fresh ? 1 : undefined })}`), [site.slot, fresh]);
  const balance = useLoad<UnlockBalanceInfo>(() => get(`/api/unlock/balance${qs({ site: site.slot })}`), [site.slot]);
  const [q, setQ] = useState('');
  const [serviceId, setServiceId] = useState(() => storageGet<string>(`${LAST_UNLOCK_SERVICE}_${site.slot}`, ''));
  const [values, setValues] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  // The tap being confirmed (its request id), and an "already ordered" answer with the order it named.
  const [confirm, setConfirm] = useState<string | null>(null);
  const [dupe, setDupe] = useState<{ message: string; orderId: string | null; requestId: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // A different phone: nothing half-confirmed carries over to it (the note and boxes stay, they may be typed first).
  useEffect(() => {
    setConfirm(null);
    setDupe(null);
  }, [imei]);

  const services = list.data?.services ?? [];
  const currency = list.data?.currency ?? balance.data?.currency ?? null;
  const shown = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length ? services.filter((sv) => words.every((w) => `${sv.name} ${sv.group}`.toLowerCase().includes(w))) : services;
  }, [services, q]);
  const chosen = services.find((sv) => sv.id === serviceId) ?? null;
  // The picked service stays in the list while a search hides the rest of its group.
  const groups = useMemo(() => {
    const m = new Map<string, UnlockServiceInfo[]>();
    for (const sv of chosen && !shown.includes(chosen) ? [chosen, ...shown] : shown) m.set(sv.group, [...(m.get(sv.group) ?? []), sv]);
    return [...m];
  }, [shown, chosen]);
  const missing = chosen ? chosen.fields.filter((f) => f.required && !(values[f.name] ?? '').trim()) : [];
  const price = chosen ? unlockPriceLabel(chosen.price, currency) : '';
  const ready = !!imei && !!chosen && !chosen.site_only && !missing.length;

  const pick = (sid: string) => {
    setServiceId(sid);
    storageSet(`${LAST_UNLOCK_SERVICE}_${site.slot}`, sid);
    setValues({});
    setConfirm(null);
    setDupe(null);
  };

  // A retry of the same tap sends the same request id, so the site never gets the order twice.
  const place = async (requestId: string, againFor: string | null) => {
    if (!chosen || !imei || busy) return;
    setBusy(true);
    try {
      const o = await post<UnlockOrderInfo>('/api/unlock-orders', { site: site.slot, service: chosen.id, imei, note: note.trim() || null, fields: values, againFor, requestId });
      setConfirm(null);
      setDupe(null);
      setNote('');
      onOrdered();
      balance.reload();
      if (o.status === 'failed') toast(o.error ?? 'The order didn’t go through.', 'error');
      else if (o.status === 'check') toast(o.error ?? `Look at your orders on ${site.name} before ordering again.`, 'error');
      else if (o.status === 'sending') toast(`Still sending this order to ${site.name}. It shows below.`, 'info');
      else toast(`Ordered on ${site.name}. The code shows here when it’s ready.`, 'good');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'conflict') {
        setConfirm(null);
        setDupe({ message: e.message, orderId: typeof e.details?.order_id === 'string' ? e.details.order_id : null, requestId: newRequestId() });
      } else {
        toast(e instanceof Error ? e.message : 'The order didn’t go through.', 'error');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="unlock-order">
      <div className="row-wrap">
        <span className="small muted grow">Ordered on your {site.name} account and paid from your credit there. The code shows below when it’s ready.</span>
        {balance.data?.balance ? <Badge tone="blue">{site.name} credit {unlockPriceLabel(balance.data.balance, balance.data.currency)}</Badge> : null}
      </div>
      {balance.data?.error ? <div className="banner warn"><Icon.alert /><div className="small">{balance.data.error}</div></div> : null}
      {list.error ? <div className="banner warn"><Icon.alert /><div className="small">{list.error.message}</div></div> : null}
      {list.data?.stale ? <p className="tiny muted">{site.name} didn’t answer just now, so this is its list from {when(list.data.fetched_at)}.</p> : null}
      {list.loading && !list.data ? <Spinner label={`Loading ${site.name}’s services`} /> : null}
      {services.length ? (
        <>
          <div className="row-wrap">
            <input
              className="input unlock-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${services.length} services: AT&T, T-Mobile, iCloud…`}
              aria-label={`Search ${site.name} services`}
            />
            <Button size="sm" icon="refresh" busy={list.loading && !!list.data} onClick={() => setFresh((n) => n + 1)} title={`Get ${site.name}’s latest services and prices`}>Refresh</Button>
          </div>
          <select className="select" value={chosen?.id ?? ''} onChange={(e) => pick(e.target.value)} aria-label={`${site.name} service`}>
            <option value="">{shown.length ? `Pick a service (${shown.length})` : 'No service matches the search'}</option>
            {groups.map(([g, items]) => (
              <optgroup key={g} label={g}>
                {items.map((sv) => (
                  <option key={sv.id} value={sv.id}>
                    {sv.name}{sv.price ? ` · ${unlockPriceLabel(sv.price, currency)}` : ''}{sv.time ? ` · ${sv.time}` : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </>
      ) : list.data ? <p className="small muted">{site.name} has no IMEI services on your account.</p> : null}

      {chosen ? (
        <div className="unlock-chosen">
          <div className="strong">{chosen.name}</div>
          <div className="small muted">{[price, chosen.time, chosen.group].filter(Boolean).join(' · ')}</div>
          {chosen.info ? <p className="small pc-text unlock-info">{chosen.info}</p> : null}
          {chosen.site_only ? (
            <div className="banner warn">
              <Icon.alert />
              <div className="small">{site.name} needs {chosen.site_only} for this service, which STORE OS can’t send. Order this one on the {site.name} site (the button above).</div>
            </div>
          ) : (
            <>
              {chosen.fields.map((f) => (
                <Field key={f.name} label={f.label} hint={f.required ? undefined : 'optional'}>
                  {f.kind === 'select' ? (
                    <select className="select" value={values[f.name] ?? ''} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}>
                      <option value="">Pick one</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input className="input" value={values[f.name] ?? ''} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} autoComplete="off" maxLength={300} />
                  )}
                </Field>
              ))}
              <Field label="Note" hint="optional: the customer’s name or ticket number">
                <input className="input" value={note} onChange={(e) => setNote(e.target.value)} autoComplete="off" maxLength={120} />
              </Field>
              {!imei ? <p className="small muted">Type or scan the phone’s IMEI at the top first.</p> : null}
              {dupe ? (
                <div className="banner warn">
                  <Icon.alert />
                  <div className="grow small">{dupe.message}</div>
                  <div className="row-wrap">
                    <Button size="sm" onClick={() => setDupe(null)}>Don’t order</Button>
                    {dupe.orderId ? <Button size="sm" variant="primary" busy={busy} onClick={() => place(dupe.requestId, dupe.orderId)}>Order again</Button> : null}
                  </div>
                </div>
              ) : confirm ? (
                <div className="banner">
                  <Icon.unlock />
                  <div className="grow small">
                    Order <span className="strong">{chosen.name}</span> for <span className="strong num">{imei}</span> on {site.name}?{' '}
                    {price ? <><span className="strong">{price}</span> comes off your {site.name} credit.</> : <>It’s paid from your {site.name} credit.</>}
                  </div>
                  <div className="row-wrap">
                    <Button size="sm" onClick={() => setConfirm(null)}>Cancel</Button>
                    <Button size="sm" variant="primary" busy={busy} onClick={() => place(confirm, null)}>Yes, order it</Button>
                  </div>
                </div>
              ) : (
                <div>
                  <Button variant="primary" icon="unlock" disabled={!ready} onClick={() => setConfirm(newRequestId())}>{`Order unlock${price ? ` · ${price}` : ''}`}</Button>
                  {missing.length ? <span className="small muted"> Fill in {missing.map((f) => f.label).join(' and ')} first.</span> : null}
                </div>
              )}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function UnlockOrderCard({ order, showImei, onChanged }: { order: UnlockOrderInfo; showImei: boolean; onChanged: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const st = UNLOCK_STATUS[order.status] ?? UNLOCK_STATUS.working;
  const openNow = order.status === 'waiting' || order.status === 'working';
  const checkNow = async () => {
    setBusy(true);
    try {
      const o = await post<UnlockOrderInfo>(`/api/unlock-orders/${order.id}/check`);
      onChanged();
      if (o.status === 'done') toast('The code is ready.', 'good');
      else if (o.status === order.status) toast(o.error ?? `${order.site_name} is still working on it.`, o.error ? 'error' : 'info');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Couldn’t check that order.', 'error');
    } finally {
      setBusy(false);
    }
  };
  const copy = () => {
    const ok = copyText(`${order.service.name} · ${order.imei}\n${order.code ?? ''}`);
    toast(ok ? 'Copied. Paste it in the ticket notes or a text to the customer.' : 'Couldn’t copy it.', ok ? 'good' : 'error');
  };
  const price = unlockPriceLabel(order.price, order.currency);
  return (
    <section className={`unlock-card unlock-${order.status}`} aria-label={`Unlock order ${order.service.name}`}>
      <div className="pc-result-head">
        <div className="grow">
          <div className="strong">{order.service.name}</div>
          <div className="small muted num">
            {showImei ? `${order.imei} · ` : ''}{order.site_name}{order.reference ? ` #${order.reference}` : ''} · {when(order.created_at)}{order.employee_name ? ` · ${order.employee_name}` : ''}{price ? ` · ${price}` : ''}
          </div>
          {order.note ? <div className="small">{order.note}</div> : null}
        </div>
        <Badge tone={st.tone}>{st.label}</Badge>
      </div>
      {order.status === 'done' && order.code ? (
        <>
          <pre className="unlock-code">{order.code}</pre>
          <div className="row-wrap"><Button size="sm" icon="list" onClick={copy}>Copy</Button></div>
        </>
      ) : null}
      {order.status === 'rejected' ? <p className="small">{order.code ? `${order.site_name} said: ${order.code}` : `${order.site_name} couldn’t do it.`} The credit goes back to your {order.site_name} account.</p> : null}
      {order.status === 'failed' || order.status === 'check' ? <p className="small">{order.error}</p> : null}
      {openNow ? (
        <div className="row-wrap">
          <span className="small muted grow">
            {order.error ? order.error : `STORE OS keeps asking ${order.site_name} on its own${order.checked_at ? `; last asked ${when(order.checked_at)}` : ''}.`}
          </span>
          <Button size="sm" icon="refresh" busy={busy} onClick={checkNow}>Check now</Button>
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------- the Phone check window

function PhoneCheckSheet({ initialImei, onClose }: { initialImei: string; onClose: () => void }) {
  const own = usePlan().home;
  const toast = useToast();
  const { can } = useSession();
  const canBuy = can('buyback.create');
  // Phones picked to buy (one check per phone), for "Buy these".
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const setup = useLoad<Setup>(() => get('/api/phone-check/setup'), []);
  const [code, setCode] = useState(initialImei);
  const [service, setService] = useState<string>(() => storageGet<string>(LAST_SERVICE, ''));
  const [result, setResult] = useState<PhoneCheck | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<{ balance: string | null; as_of: number | null } | null>(null);
  // "Check many at once": a list of IMEIs, checked two at a time, with a result per phone.
  const [many, setMany] = useState(false);
  const [codes, setCodes] = useState('');
  const [bulk, setBulk] = useState<BulkRow[] | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const id = cleanDeviceId(code);
  const problem = id ? deviceIdProblem(id) : null;
  const showProblem = !!problem && (id.length >= 10 || /^\d{15}$/.test(id));
  const phoneId = useDebounced(id && !problem ? id : '', 300);
  const history = useLoad<PhoneCheck[]>(() => get(`/api/phone-checks${qs({ imei: phoneId || undefined, limit: many ? 100 : 20 })}`), [phoneId, many]);
  // The newest check of each phone in the list: what "Select all" picks.
  const perPhone = useMemo(() => {
    const seen = new Set<string>();
    return (history.data ?? []).filter((h) => (seen.has(h.imei) ? false : (seen.add(h.imei), true)));
  }, [history.data]);
  const pickedChecks = (history.data ?? []).filter((h) => picked.has(h.id));
  const pickedCount = new Set(pickedChecks.map((h) => h.imei)).size;
  const allPicked = perPhone.length > 0 && perPhone.every((h) => picked.has(h.id));
  const togglePick = (h: PhoneCheck) => setPicked((cur) => {
    const next = new Set(cur);
    if (next.has(h.id)) next.delete(h.id);
    else {
      // One check per phone: picking another check of the same phone replaces it.
      for (const o of history.data ?? []) if (o.imei === h.imei) next.delete(o.id);
      next.add(h.id);
    }
    return next;
  });
  const buyPicked = () => {
    if (!pickedChecks.length) return;
    onClose();
    navigate(`/buy-many?checks=${pickedChecks.map((h) => h.id).join(',')}`);
  };

  const ready = !!setup.data?.ready;
  const services = setup.data?.services ?? [];
  const chosen = services.find((s) => s.id === service) ?? services.find((s) => s.id === setup.data?.buy_service) ?? services.find((s) => s.id === '61') ?? services[0];
  const quick = services.filter((s) => s.quick);
  const groups = GROUP_ORDER.map((g) => [g, services.filter((s) => s.group === g)] as const).filter(([, list]) => list.length);
  // A result stays on screen only while it's for the phone typed in.
  const shown = result && result.imei === id ? result : null;

  useEffect(() => {
    if (!ready) return;
    get<{ balance: string | null; as_of: number | null }>('/api/phone-check/balance').then(setBalance).catch(() => undefined);
  }, [ready]);

  const pick = (sid: string) => {
    setService(sid);
    storageSet(LAST_SERVICE, sid);
    setError(null);
  };

  const run = async (fresh = false) => {
    if (!chosen || !id || problem || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await post<PhoneCheck>('/api/phone-checks', { imei: id, service: chosen.id, fresh });
      setResult(r);
      if (r.balance) setBalance({ balance: r.balance, as_of: r.created_at });
      history.reload();
      if (r.status === 'error') toast('The check didn’t work. The reason is below.', 'error');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The check didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  // The list typed or scanned for "Check many at once": one code per line (commas and spaces work too), no repeats.
  const parsedCodes = useMemo(() => {
    const seen = new Set<string>();
    return codes.split(/[\s,;]+/).map((c) => cleanDeviceId(c)).filter((c) => !!c && !seen.has(c) && (seen.add(c), true));
  }, [codes]);
  const goodCodes = parsedCodes.filter((c) => !deviceIdProblem(c));
  const badCount = parsedCodes.length - goodCodes.length;
  const bulkDone = bulk ? bulk.filter((r) => r.state === 'done' || r.state === 'failed').length : 0;
  const bulkCost = chosen ? Number(chosen.last_price ?? chosen.price ?? 0) * goodCodes.length : 0;

  const runMany = async () => {
    if (!chosen || !goodCodes.length || bulkBusy) return;
    const service = chosen.id;
    const rows: BulkRow[] = parsedCodes.map((imei): BulkRow => {
      const p = deviceIdProblem(imei);
      return { imei, state: p ? 'failed' : 'waiting', check: null, error: p ?? null };
    });
    setBulk(rows);
    setBulkBusy(true);
    setError(null);
    const queue = rows.map((_, i) => i).filter((i) => rows[i].state === 'waiting');
    // Two at a time: quicker than one by one, gentle on SICKW. A phone checked today comes back saved, free.
    const worker = async () => {
      for (let i = queue.shift(); i !== undefined; i = queue.shift()) {
        rows[i] = { ...rows[i], state: 'working' };
        setBulk([...rows]);
        try {
          const c = await post<PhoneCheck>('/api/phone-checks', { imei: rows[i].imei, service, fresh: false });
          rows[i] = { ...rows[i], state: 'done', check: c };
          if (c.balance) setBalance({ balance: c.balance, as_of: c.created_at });
        } catch (e) {
          rows[i] = { ...rows[i], state: 'failed', error: e instanceof Error ? e.message : 'The check didn’t work.' };
        }
        setBulk([...rows]);
      }
    };
    await Promise.all([worker(), worker()]);
    setBulkBusy(false);
    history.reload();
  };

  const openPast = async (h: PhoneCheck) => {
    try {
      const full = await get<PhoneCheck>(`/api/phone-checks/${h.id}`);
      setCode(full.imei);
      setResult(full);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That check couldn’t be opened.', 'error');
    }
  };

  const leave = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <Sheet title="Phone check" wide onClose={onClose} footer={<Button onClick={onClose}>Close</Button>}>
      <div className="pc-intro">
        <p className="small muted grow">Runs on SICKW with the store’s account, without leaving STORE OS. Every result is saved here.</p>
        {balance?.balance ? <Badge tone="blue">SICKW balance ${Number(balance.balance).toFixed(2)}</Badge> : null}
      </div>

      {setup.error ? <div className="banner warn"><Icon.alert /><div>{setup.error.message}</div></div> : null}
      {setup.data && !ready ? (
        <div className="banner warn">
          <Icon.alert />
          <div className="grow">
            <div className="strong">SICKW isn’t connected yet.</div>
            <div className="small">
              {setup.data.can_set_up ? 'Type your SICKW API key in Settings → Buy & trade-in to turn on checks.' : 'Ask the owner to add the SICKW API key in Settings.'} The unlock sites below work already.
            </div>
          </div>
          {setup.data.can_set_up ? <Button size="sm" onClick={() => leave('/settings/buy')}>Open Settings</Button> : null}
        </div>
      ) : null}

      {many ? (
        <div className="stack-sm">
          <Field label="IMEIs or serial numbers, one per line" hint="scan them one after another, or paste a list">
            <textarea
              className="textarea num"
              rows={6}
              value={codes}
              onChange={(e) => setCodes(e.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder={'356789104512364\n354113330249916\n…'}
            />
          </Field>
          <div className="row-wrap">
            <span className="small muted grow">
              {parsedCodes.length ? `${goodCodes.length} phone${goodCodes.length === 1 ? '' : 's'} to check${badCount ? `, ${badCount} line${badCount === 1 ? '' : 's'} that don’t look like an IMEI or serial` : ''}` : 'Nothing to check yet.'}
              {chosen && goodCodes.length ? ` · about $${bulkCost.toFixed(2)} on SICKW (a phone checked today costs nothing again) · up to 120 checks an hour per person` : ''}
            </span>
            <Button size="sm" variant="ghost" onClick={() => setMany(false)}>One phone at a time</Button>
          </div>
        </div>
      ) : (
        <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); run(); }}>
          <Field label="IMEI or serial number" hint={showProblem ? undefined : 'scan or type it; dial *#06# on the phone to see the IMEI'}>
            <input
              className="input input-lg num"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              aria-invalid={showProblem}
              placeholder="15-digit IMEI or serial"
            />
          </Field>
          {showProblem ? <div className="error-text">{problem}</div> : null}
          <div className="row-wrap"><Button size="sm" variant="ghost" icon="list" onClick={() => setMany(true)}>Check many at once</Button></div>
        </form>
      )}

      {setup.loading && !setup.data ? <Spinner label="Loading the checks" /> : null}
      {services.length ? (
        <div className="stack-sm">
          <span className="small strong">Which check</span>
          <div className="chips" role="group" aria-label="Most used checks">
            {quick.map((s) => (
              <button key={s.id} type="button" className="chip" aria-pressed={chosen?.id === s.id} onClick={() => pick(s.id)}>
                {s.quick}
                {priceOf(s) ? <>{' '}<span className="pc-price">{priceOf(s)}</span></> : null}
              </button>
            ))}
          </div>
          <select className="select" value={chosen?.id ?? ''} onChange={(e) => pick(e.target.value)} aria-label="All SICKW checks">
            {groups.map(([g, list]) => (
              <optgroup key={g} label={SICKW_GROUP_LABELS[g]}>
                {list.map((s) => <option key={s.id} value={s.id}>{s.name}{priceOf(s) ? ` · ${priceOf(s)}` : ''}</option>)}
              </optgroup>
            ))}
          </select>
          <span className="tiny muted">Prices are SICKW’s list prices. What you paid shows with each result.</span>
        </div>
      ) : null}

      <div className="row-wrap">
        {many ? (
          <Button variant="primary" size="lg" icon="search" className="pc-run" disabled={!ready || !chosen || !goodCodes.length || bulkBusy} onClick={runMany}>
            {bulkBusy ? `Checking ${bulkDone} of ${bulk?.length ?? 0}…` : chosen ? `Check ${goodCodes.length || ''} phone${goodCodes.length === 1 ? '' : 's'}: ${chosen.quick ?? chosen.name}` : 'Check'}
          </Button>
        ) : (
          <Button variant="primary" size="lg" icon="search" className="pc-run" busy={busy} disabled={!ready || !chosen || !id || !!problem} onClick={() => run()}>
            {chosen ? `Check: ${chosen.quick ?? chosen.name}${priceOf(chosen) ? ` · ${priceOf(chosen)}` : ''}` : 'Check'}
          </Button>
        )}
        {error ? <span className="error-text" role="alert">{error}</span> : null}
      </div>

      {many && bulk ? (
        <div className="stack-sm" aria-live="polite">
          <span className="small strong">
            {bulkDone < bulk.length ? `Checking ${bulkDone} of ${bulk.length}…` : `${bulk.length} checked`}
            {' · '}{bulk.filter((r) => r.check?.status === 'ok' && r.check.verdict === 'clean').length} clean
            {bulk.some((r) => r.check && (r.check.status !== 'ok' || r.check.verdict !== 'clean')) ? ` · ${bulk.filter((r) => r.check && (r.check.status !== 'ok' || r.check.verdict !== 'clean')).length} to look at` : ''}
            {bulk.some((r) => r.state === 'failed' && !r.check) ? ` · ${bulk.filter((r) => r.state === 'failed' && !r.check).length} didn’t run` : ''}
          </span>
          <div className="list">
            {bulk.map((r) => {
              const v = r.check ? VERDICT[r.check.status === 'error' ? 'error' : r.check.verdict] : null;
              const p = r.check && r.check.status === 'ok' ? phoneFromCheck(r.check, null) : null;
              const words = p ? [stickerName(p.brand, p.model), p.storage, p.color, p.carrier].filter(Boolean).join(' · ') : '';
              return (
                <button key={r.imei} type="button" className="list-row" disabled={!r.check} aria-current={shown?.id === r.check?.id ? 'true' : undefined} onClick={() => { if (r.check) { setCode(r.imei); setResult(r.check); } }}>
                  <span className="main-col">
                    <span className="t num" style={{ display: 'block' }}>{r.imei}</span>
                    <span className="s" style={{ display: 'block' }}>
                      {r.state === 'waiting' ? 'Waiting…' : r.state === 'working' ? 'Checking…' : r.error ?? (words || r.check?.text?.slice(0, 80) || '')}
                      {r.check?.price ? (Number(r.check.price) === 0 ? ' · free' : ` · ${sickwPriceLabel(r.check.price)}`) : ''}{r.check?.reused ? ' · saved result, not charged' : ''}
                    </span>
                  </span>
                  {v ? <Badge tone={v.tone}>{v.label}</Badge> : r.state === 'failed' ? <Badge tone="amber">Didn’t run</Badge> : null}
                  {r.check ? <Icon.chevronRight /> : null}
                </button>
              );
            })}
          </div>
          {bulkDone === bulk.length && bulk.some((r) => r.check) ? <p className="small muted">Tap a phone for its full result and price sticker. Every result is also saved under Latest checks below, where you can tick them and buy the phones in one go.</p> : null}
        </div>
      ) : null}

      {shown ? <CheckResult key={shown.id} check={shown} busy={busy} onRunAgain={() => run(true)} onLeave={leave} /> : null}

      {/* The unlock sites and unlock orders use West Coast Wireless's own accounts: its store only, for now. */}
      {own ? (
        <>
          <div className="stack-sm">
            <span className="small strong">Unlock sites</span>
            <UnlockSites imei={id} onLeave={onClose} />
          </div>
          <UnlockOrders imei={phoneId} onLeave={onClose} />
        </>
      ) : null}

      <div className="stack-sm">
        <span className="small strong">{phoneId ? 'Checks for this phone' : 'Latest checks'}</span>
        {canBuy && history.data?.length ? (
          <div className="pc-buybar">
            <label className="check"><input type="checkbox" checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(perPhone.map((h) => h.id)))} /> Select all</label>
            {pickedCount ? (
              <Button variant="primary" icon="phoneCash" onClick={buyPicked}>{pickedCount === 1 ? 'Buy this phone' : `Buy these ${pickedCount} phones`}</Button>
            ) : <span className="small muted">Tick the phones the customer is selling you, then buy them in one go.</span>}
          </div>
        ) : null}
        {history.data?.length ? (
          <div className="list">
            {history.data.map((h) => {
              const v = VERDICT[h.status === 'error' ? 'error' : h.verdict];
              return (
                <div key={h.id} className="pc-row">
                  {canBuy ? (
                    <label className="pc-pick" title="Pick this phone to buy">
                      <input type="checkbox" checked={picked.has(h.id)} onChange={() => togglePick(h)} aria-label={`Pick ${h.imei} to buy`} />
                    </label>
                  ) : null}
                  <button type="button" className="list-row" aria-current={shown?.id === h.id ? 'true' : undefined} onClick={() => openPast(h)}>
                    <span className="main-col">
                      <span className="t" style={{ display: 'block' }}>{h.service.name}</span>
                      <span className="s" style={{ display: 'block' }}>
                        {phoneId ? '' : `${h.imei} · `}{when(h.created_at)}{h.employee_name ? ` · ${h.employee_name}` : ''}{h.price ? ` · ${sickwPriceLabel(h.price)}` : ''}
                      </span>
                    </span>
                    {v ? <Badge tone={v.tone}>{v.label}</Badge> : null}
                    <Icon.chevronRight />
                  </button>
                </div>
              );
            })}
          </div>
        ) : history.data ? (
          <p className="small muted">{phoneId ? 'No checks for this phone yet.' : 'No checks yet.'}</p>
        ) : history.loading ? <Spinner label="Loading checks" /> : null}
      </div>
    </Sheet>
  );
}

function CheckResult({ check, busy, onRunAgain, onLeave }: { check: PhoneCheck; busy: boolean; onRunAgain: () => void; onLeave: (to: string) => void }) {
  const toast = useToast();
  const { can } = useSession();
  const canAdd = can('device.add');
  // The price sticker opens right under every result that worked, filled in from SICKW; its button hides and shows it.
  const [sticker, setSticker] = useState(true);
  // "Add to inventory" opens the Add a phone window, filled in from this check.
  const [adding, setAdding] = useState(false);
  const failed = check.status === 'error';
  const v = VERDICT[failed ? 'error' : check.verdict];
  const lines = check.lines ?? [];
  const copy = () => {
    const ok = copyText(`${check.service.name} · ${check.imei}\n${formatDateTime(check.created_at)}\n${check.text}`);
    toast(ok ? 'Result copied. Paste it in the ticket notes.' : 'Couldn’t copy the result.', ok ? 'good' : 'error');
  };
  return (
    <section className={`pc-result ${failed ? 'pc-failed' : ''}`} aria-label="Check result" aria-live="polite">
      <div className="pc-result-head">
        <div className="grow">
          <div className="strong">{check.service.name}</div>
          <div className="small muted num">
            {check.imei} · {when(check.created_at)}{check.employee_name ? ` · ${check.employee_name}` : ''}{check.price ? (Number(check.price) === 0 ? ' · free' : ` · paid ${sickwPriceLabel(check.price)}`) : ''}
          </div>
        </div>
        {v ? <Badge tone={v.tone}>{v.label}</Badge> : null}
      </div>
      {check.reused ? (
        <div className="banner">
          <Icon.history />
          <div className="grow small">This check already ran {when(check.created_at)}. This is the saved result, so you weren’t charged again.</div>
          <Button size="sm" busy={busy} onClick={onRunAgain}>Run again</Button>
        </div>
      ) : null}
      {failed ? (
        <p>{check.text}</p>
      ) : lines.length ? (
        <dl className="pc-lines">
          {lines.map((l, i) => (
            <Fragment key={i}>
              {l.label ? <dt>{l.label}</dt> : null}
              <dd className={`${l.label ? '' : 'pc-full '}${l.tone ? `pc-${l.tone}` : ''}`}>{l.value}</dd>
            </Fragment>
          ))}
        </dl>
      ) : (
        <pre className="small pc-text">{check.text}</pre>
      )}
      {!failed ? (
        <div className="row-wrap">
          <Button size="sm" icon="list" onClick={copy}>Copy result</Button>
          <Button size="sm" variant={sticker ? 'secondary' : 'primary'} icon="tag" aria-expanded={sticker} onClick={() => setSticker((v) => !v)}>
            {sticker ? 'Hide the price sticker' : 'Use SICKW info & print price sticker'}
          </Button>
          {canAdd ? (
            <Button size="sm" icon="plus" aria-expanded={adding} onClick={() => setAdding((v) => !v)} title="Put this phone in inventory with the model, IMEI and carrier filled in from SICKW">
              Add to inventory
            </Button>
          ) : null}
        </div>
      ) : null}
      {sticker && !failed ? <SickwSticker check={check} onClose={() => setSticker(false)} onLeave={onLeave} /> : null}
      {adding && !failed ? <SickwAddToInventory check={check} onClose={() => setAdding(false)} onLeave={onLeave} /> : null}
    </section>
  );
}

// ---------------------------------------------------------------- the price sticker from a check

interface DeviceCatalog { models?: Record<string, string[]>; storages?: Record<string, string[]>; carriers?: string[] | null }
interface StockPhone { id: string; title: string; status: string; imei: string | null; imei2: string | null; serial: string | null; price_cents: number; battery_health: number | null }

/** The store's models (and their sizes) and carriers, as the sticker matches SICKW's words to them. */
function modelList(c: DeviceCatalog | null): ModelList | null {
  if (!c?.models) return null;
  const storages = c.storages ?? {};
  return {
    brands: Object.fromEntries(Object.entries(c.models).map(([b, ms]) => [b, Object.fromEntries(ms.map((m) => [m, storages[`${b}|${m}`] ?? []]))])),
    carriers: c.carriers ?? null,
  };
}

/** This phone in the store's inventory (not sold), for its price and battery health. */
function stockMatch(list: StockPhone[], ids: (string | null)[]): StockPhone | null {
  const want = new Set(ids.filter(Boolean).map((x) => x!.toUpperCase()));
  const live = ['in_stock', 'testing', 'on_hold', 'in_transit'];
  const hits = list.filter((d) => live.includes(d.status) && [d.imei, d.imei2, d.serial].some((x) => !!x && want.has(x.toUpperCase())));
  return hits.find((d) => d.status === 'in_stock') ?? hits[0] ?? null;
}

/**
 * The 3 × 2 in price sticker, filled in from this check. It opens on its own under every result
 * that worked. The price is remembered: what the phone is on sale for when it's in inventory, else
 * what the store last asked for one like it (same model, storage and carrier).
 */
function SickwSticker({ check, onClose, onLeave }: { check: PhoneCheck; onClose: () => void; onLeave: (to: string) => void }) {
  const info = useLoad(async () => {
    const [catalog, stock] = await Promise.all([
      get<DeviceCatalog>('/api/device-catalog').catch(() => null),
      get<StockPhone[]>(`/api/devices${qs({ q: check.imei, status: 'any', limit: 10 })}`).catch(() => [] as StockPhone[]),
    ]);
    const list = modelList(catalog);
    const phone = phoneFromCheck(check, list);
    const inStock = stockMatch(stock, [phone.imei, phone.serial, cleanDeviceId(check.imei)]);
    const like = inStock ? null : await lastPriceLike({ brand: phone.brand, model: phone.model, storage: phone.storage, carrier: phone.carrier, locked: phone.lock === 'locked' });
    return { list, phone, inStock, like };
  }, [check.id]);
  if (info.loading || !info.data) return <div className="pc-sticker"><Spinner label="Reading the check" /></div>;
  const { list, phone, inStock, like } = info.data;
  const carriers = (list?.carriers?.length ? list.carriers : CARRIERS).filter((c) => !/^(unlocked|other( carrier)?)$/i.test(c));
  return <SickwStickerForm check={check} phone={phone} carriers={carriers} stock={inStock} like={like} onClose={onClose} onLeave={onLeave} />;
}

// ---------------------------------------------------------------- add to inventory from a check

/** What the Add a phone window starts with, from a check: the phone as SICKW describes it. */
function prefillFromCheck(check: PhoneCheck, phone: CheckedPhone): DevicePrefill {
  const lines = check.lines ?? [];
  const imei2 = (lines.find((l) => /^imei ?2$/i.test(l.label))?.value ?? '').replace(/\D/g, '');
  const serialLine = (lines.find((l) => /^serial( number)?$/i.test(l.label))?.value ?? '').trim();
  const words = `${phone.brand} ${phone.model}`;
  const categoryId = /ipad/i.test(words) ? 'ipads' : /macbook/i.test(words) ? 'macbooks' : /\btab\b|tablet/i.test(words) ? 'tablets' : 'phones';
  const v = VERDICT[check.verdict]?.label;
  return {
    categoryId, brand: phone.brand, model: phone.model, storage: phone.storage, color: phone.color,
    carrier: phone.carrier || (phone.lock === 'unlocked' ? 'Unlocked' : ''), locked: phone.lock === 'locked',
    imei: phone.imei ?? '', imei2: isValidImei(imei2) && imei2 !== phone.imei ? imei2 : '', serial: phone.serial ?? serialLine,
    notes: `SICKW ${check.service.name}, ${formatDateTime(check.created_at)}${v ? `: ${v}` : ''}`,
  };
}

/**
 * "Add to inventory": the Add a phone window, filled in from this check (model, storage, color,
 * carrier and lock, IMEI, serial). When the phone is already in stock it says so instead.
 */
function SickwAddToInventory({ check, onClose, onLeave }: { check: PhoneCheck; onClose: () => void; onLeave: (to: string) => void }) {
  const catalog = useLoad<DeviceCatalog | null>(() => get<DeviceCatalog>('/api/device-catalog').catch(() => null), []);
  const stock = useLoad<StockPhone[]>(() => get<StockPhone[]>(`/api/devices${qs({ q: check.imei, status: 'any', limit: 10 })}`).catch(() => []), [check.imei]);
  const [added, setAdded] = useState<StockPhone | null>(null);
  if (catalog.loading || stock.loading) return <div className="pc-sticker"><Spinner label="Reading the check" /></div>;
  const phone = phoneFromCheck(check, modelList(catalog.data));
  const inStock = added ?? stockMatch(stock.data ?? [], [phone.imei, phone.serial, cleanDeviceId(check.imei)]);
  if (inStock) {
    return (
      <div className="banner">
        <Icon.boxes />
        <div className="grow small">
          {added ? 'Added to inventory: ' : 'Already in your inventory: '}<span className="strong">{inStock.title}</span>{inStock.price_cents ? <> at {formatCents(inStock.price_cents)}</> : null}.
        </div>
        <Button size="sm" onClick={() => onLeave(`/inventory/devices?id=${inStock.id}`)}>Open in Inventory</Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
      </div>
    );
  }
  // Over the Phone check window, like the price sticker in Inventory.
  return createPortal(<AddDeviceSheet prefill={prefillFromCheck(check, phone)} onClose={onClose} onDone={(d) => (d ? setAdded(d as StockPhone) : onClose())} />, document.body);
}

function SickwStickerForm({ check, phone, carriers, stock, like, onClose, onLeave }: {
  check: PhoneCheck; phone: CheckedPhone; carriers: string[]; stock: StockPhone | null; like: PricedLike | null; onClose: () => void; onLeave: (to: string) => void;
}) {
  const { me } = useSession();
  const print = usePrint();
  // Filled in from SICKW, and the price from inventory when the phone is in stock, else the price
  // of the last one like it. The battery % is always left blank: staff read it off the phone
  // (Settings → Battery) and type it in.
  const [draft, setDraft] = useState<StickerDraft>(() => ({
    name: stickerName(phone.brand, phone.model), storage: phone.storage, color: phone.color, carrier: phone.carrier, lock: phone.lock ?? '',
    price: stock?.price_cents ? stock.price_cents : like?.price_cents ?? null, battery: '',
  }));
  const ids = { imei: phone.imei, serial: phone.serial };
  const ready = !!draft.name.trim() && !!draft.price && draft.price > 0 && !batteryProblem(draft.battery);
  const risky = check.verdict === 'blocked' ? 'SICKW says this phone is blacklisted, lost or stolen.' : check.verdict === 'fmi_on' ? 'SICKW says Find My (iCloud) is still on.' : null;
  return (
    <div className="pc-sticker" role="group" aria-label="Price sticker from this check">
      <div className="pc-result-head">
        <div className="grow">
          <div className="strong">Price sticker from this check</div>
          <div className="small muted">Filled in from SICKW. Check it, add the price and the battery % off the phone, and print it on the Rollo (3 × 2 in labels).</div>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
      </div>
      {risky ? <div className="banner warn"><Icon.alert /><div className="small">{risky} Sort that out before you sell it.</div></div> : null}
      {stock ? (
        <div className="banner">
          <Icon.boxes />
          <div className="grow small">
            In your inventory: {stock.title} at <span className="strong">{formatCents(stock.price_cents)}</span>{stock.battery_health ? `, battery ${stock.battery_health}%` : ''}.
            {draft.price !== null && draft.price !== stock.price_cents ? <> The register charges {formatCents(stock.price_cents)} when this IMEI is scanned, so change the price in Inventory too.</> : null}
          </div>
          <Button size="sm" onClick={() => onLeave(`/inventory/devices?id=${stock.id}`)}>Open in Inventory</Button>
        </div>
      ) : like ? (
        <div className="banner">
          <Icon.boxes />
          <div className="grow small">
            Priced like the last one: <span className="strong">{like.title}</span> at <span className="strong">{formatCents(like.price_cents)}</span>{like.status === 'sold' ? ', sold at the register' : ', in stock'}. Change the price if this one’s different.
          </div>
        </div>
      ) : null}
      <StickerEditor draft={draft} onChange={setDraft} ids={ids} carriers={carriers} priceHint={!stock && like && draft.price === like.price_cents ? `the last one like it: ${formatCents(like.price_cents)}` : undefined} />
      <div className="row-wrap">
        <Button variant="primary" icon="tag" disabled={!ready} onClick={() => printPriceStickers(print, [stickerFromDraft(draft, ids)], me?.settings)}>Print price sticker</Button>
        {!draft.price ? <span className="small muted">Add the price first.</span> : null}
      </div>
    </div>
  );
}
